import { loadStripe } from '@stripe/stripe-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { defaultToastProps } from 'appConstants';
import { SPRITE_TEST_KEY } from 'configure';
import { toast } from 'react-toastify';

import {
  BillingInfoRequestSchema,
  PaymentOperationBody,
  StartFreeTrialBody,
  SubscriptionType,
} from './types';

const getSubscriptionKey = 'getSubscriptionKey';
const configureStripeKey = 'configureStripeKey';
const getPricesKey = 'getPricesKey';
const getPaymentsKey = 'getPaymentsKey';

export const useConfigureStripe = () => {
  return useQuery({
    queryKey: [configureStripeKey],
    queryFn: () => loadStripe(SPRITE_TEST_KEY),
    refetchOnWindowFocus: false,
    staleTime: 6000,
    retry: false,
  });
};

export const useGetSubscription = () => {
  return useQuery({
    queryKey: [getSubscriptionKey],
    queryFn: () => API.v1.subscription.getSubscription().then((res) => res.data),
    refetchOnWindowFocus: false,
    staleTime: 6000,
    retry: false,
  });
};

export const useGetPaymentsList = () => {
  return useQuery({
    queryKey: [getPaymentsKey],
    queryFn: () => API.v1.subscription.getPaymentList().then((res) => res.data),
    refetchOnWindowFocus: false,
    staleTime: 6000,
    retry: false,
  });
};

export const useGetPrices = () => {
  return useQuery({
    queryKey: [getPricesKey],
    queryFn: () => API.v1.subscription.getPriceList().then((res) => res.data),
    refetchOnWindowFocus: false,
    staleTime: 6000,
    retry: false,
  });
};

export const useUpdateBillingInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: BillingInfoRequestSchema) => {
      return API.v1.subscription.updateBillingInfo(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
      toast('Billing info successfully update', {
        ...defaultToastProps,
        type: 'success',
      });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (methodId: string) => {
      return API.v1.subscription.deletePaymentMethod(methodId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getPaymentsKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useSetDefaultPayment = () => {
  const queryClient = useQueryClient();
  const subscription = queryClient.getQueryData<SubscriptionType>([getSubscriptionKey]);

  return useMutation({
    mutationFn: (methodId: string) => {
      const body: PaymentOperationBody = {
        customer_id: subscription?.id || '',
        payment_method_id: methodId,
      };
      return API.v1.subscription.setPaymentDefault(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getPaymentsKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useStartFreeTrial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: StartFreeTrialBody) => {
      return API.v1.subscription.startFreeTrial(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useRenewSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: StartFreeTrialBody) => {
      return API.v1.subscription.renewSubscription(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useResumeSubscription = () => {
  const queryClient = useQueryClient();
  const subscription = queryClient.getQueryData<SubscriptionType>([getSubscriptionKey]);

  return useMutation({
    mutationFn: () => {
      const activeSubscription = subscription?.subscriptions.find(
        (sub) => sub.product_type.toLowerCase() === 'menu',
      );
      if (!activeSubscription) {
        throw new Error('Subscription not found');
      }
      return API.v1.subscription.resumeSubscription(
        activeSubscription.stripe_subscription_id,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();
  const subscription = queryClient.getQueryData<SubscriptionType>([getSubscriptionKey]);

  return useMutation({
    mutationFn: (newPriceId: string) => {
      const activeSubscription = subscription?.subscriptions.find(
        (sub) => sub.product_type.toLowerCase() === 'menu',
      );
      if (!activeSubscription) {
        throw new Error('Subscription not found');
      }
      return API.v1.subscription.upgradeSubscription(
        activeSubscription.stripe_subscription_id,
        newPriceId,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  const subscription = queryClient.getQueryData<SubscriptionType>([getSubscriptionKey]);

  return useMutation({
    mutationFn: () => {
      const activeSubscription = subscription?.subscriptions.find(
        (sub) => sub.product_type.toLowerCase() === 'menu',
      );
      if (!activeSubscription) {
        throw new Error('Subscription not found');
      }
      return API.v1.subscription.cancelSubscription(
        activeSubscription.stripe_subscription_id,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [getSubscriptionKey] });
    },
    onError: (error) => {
      toast(error.message, {
        ...defaultToastProps,
        type: 'error',
      });
    },
  });
};

export const returnActiveSubscription = () => {
  const { data: subscription } = useGetSubscription();
  const activeSubscription = subscription?.subscriptions.find(
    (sub) => sub.product_type.toLowerCase() === 'menu',
  );
  const isSubscriptionInvalid =
    activeSubscription?.status.toLocaleLowerCase() === 'pastdue' ||
    activeSubscription?.status.toLocaleLowerCase() === 'cancel';

  return { isSubscriptionInvalid };
};
